import React, {useEffect, useRef, useState} from "react";
import {renderStrokes} from "./strokeRenderer";

// Hand-authored test recording: a few strokes, an undo, more strokes, a clear,
// then a final flourish. Coordinates are tuned for a 500x500 canvas.
const SAMPLE_RECORDING = {
    v: 1,
    w: 500,
    h: 500,
    events: [
        {t: 'stroke', ts: 0,    c: '#FFFFFF', s: 10, pts: arcPoints(150, 250, 80, 0, Math.PI, 24)},
        {t: 'stroke', ts: 1500, c: '#FFDC00', s: 10, pts: linePoints(180, 200, 320, 200, 20)},
        {t: 'stroke', ts: 2400, c: '#1F75FE', s: 10, pts: linePoints(250, 100, 250, 400, 30)},
        {t: 'undo',   ts: 3500},
        {t: 'stroke', ts: 4000, c: '#EE204D', s: 10, pts: arcPoints(250, 250, 120, Math.PI, Math.PI * 2, 30)},
        {t: 'clear',  ts: 5800},
        {t: 'stroke', ts: 6300, c: '#FFFFFF', s: 10, pts: linePoints(100, 100, 400, 400, 30)},
        {t: 'stroke', ts: 7100, c: '#1CAC78', s: 10, pts: linePoints(400, 100, 100, 400, 30)},
        {t: 'stroke', ts: 8000, c: '#FF7538', s: 10, pts: arcPoints(250, 250, 60, 0, Math.PI * 2, 36)},
    ],
};

function linePoints(x1, y1, x2, y2, n) {
    const pts = [];
    for (let i = 0; i <= n; i++) {
        const t = i / n;
        pts.push([Math.round(x1 + (x2 - x1) * t), Math.round(y1 + (y2 - y1) * t)]);
    }
    return pts;
}

function arcPoints(cx, cy, r, a0, a1, n) {
    const pts = [];
    for (let i = 0; i <= n; i++) {
        const t = i / n;
        const a = a0 + (a1 - a0) * t;
        pts.push([Math.round(cx + Math.cos(a) * r), Math.round(cy + Math.sin(a) * r)]);
    }
    return pts;
}

const FETCH_TOKEN = 'wobble';
const FETCH_DATA_TYPE = 'INITIAL_DRAWING';

export const PlaybackPrototype = () => {
    const canvasRef = useRef(null);
    const handleRef = useRef(null);
    const [targetMs, setTargetMs] = useState(6000);
    const [recordingText, setRecordingText] = useState(JSON.stringify(SAMPLE_RECORDING, null, 2));
    const [error, setError] = useState(null);
    const [playing, setPlaying] = useState(false);
    const [fetchStatus, setFetchStatus] = useState(`fetching ${FETCH_TOKEN} / ${FETCH_DATA_TYPE}…`);

    useEffect(() => {
        const params = new URLSearchParams({token: FETCH_TOKEN, data_type: FETCH_DATA_TYPE});
        fetch(`/dancelestrations/get-data?${params}`)
            .then((r) => r.json())
            .then((data) => {
                if (!data || !data.value) {
                    setFetchStatus(`no recording found for ${FETCH_TOKEN} / ${FETCH_DATA_TYPE}`);
                    return;
                }
                setRecordingText(data.value);
                setFetchStatus(`loaded ${FETCH_TOKEN} / ${FETCH_DATA_TYPE} (${data.value.length} chars)`);
            })
            .catch((err) => setFetchStatus(`fetch failed: ${err.message}`));
    }, []);

    const play = () => {
        let recording;
        try {
            recording = JSON.parse(recordingText);
        } catch (err) {
            setError(`Invalid JSON: ${err.message}`);
            return;
        }
        setError(null);
        if (handleRef.current) handleRef.current.cancel();
        const canvas = canvasRef.current;
        canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
        setPlaying(true);
        const handle = renderStrokes(canvas, recording, {
            animated: true,
            targetMs,
            onComplete: () => setPlaying(false),
        });
        handleRef.current = handle;
    };

    const snap = () => {
        let recording;
        try {
            recording = JSON.parse(recordingText);
        } catch (err) {
            setError(`Invalid JSON: ${err.message}`);
            return;
        }
        setError(null);
        if (handleRef.current) handleRef.current.cancel();
        const canvas = canvasRef.current;
        renderStrokes(canvas, recording, {animated: false});
    };

    useEffect(() => () => {
        if (handleRef.current) handleRef.current.cancel();
    }, []);

    return <div style={{
        minHeight: '100dvh',
        boxSizing: 'border-box',
        padding: 24,
        backgroundColor: '#111',
        color: 'white',
        fontFamily: 'monospace',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
    }}>
        <h1 style={{margin: 0}}>Dancelestrations Playback Prototype</h1>

        <div style={{display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap'}}>
            <canvas
                ref={canvasRef}
                width={500}
                height={500}
                style={{
                    width: 500,
                    height: 500,
                    backgroundColor: '#0a0a0a',
                    border: '3px solid white',
                    borderRadius: 6,
                }}
            />

            <div style={{display: 'flex', flexDirection: 'column', gap: 12, minWidth: 360}}>
                <div style={{display: 'flex', gap: 8, alignItems: 'center'}}>
                    <button onClick={play} disabled={playing} style={btn}>▶ Play (animated)</button>
                    <button onClick={snap} style={btn}>⏭ Snap (final state)</button>
                </div>

                <label style={{display: 'flex', flexDirection: 'column', gap: 4}}>
                    <span>Target ms (animated): {targetMs}</span>
                    <input
                        type="range"
                        min={1000}
                        max={20000}
                        step={500}
                        value={targetMs}
                        onChange={(e) => setTargetMs(Number(e.target.value))}
                    />
                </label>

                <label style={{display: 'flex', flexDirection: 'column', gap: 4}}>
                    <span>Recording JSON</span>
                    <textarea
                        value={recordingText}
                        onChange={(e) => setRecordingText(e.target.value)}
                        rows={20}
                        spellCheck={false}
                        style={{
                            fontFamily: 'monospace',
                            fontSize: 12,
                            backgroundColor: '#000',
                            color: '#0f0',
                            border: '1px solid #555',
                            padding: 8,
                            borderRadius: 4,
                        }}
                    />
                </label>

                <div style={{fontSize: 13, opacity: 0.75}}>{fetchStatus}</div>
                {error && <div style={{color: '#ff5c5c'}}>{error}</div>}
            </div>
        </div>
    </div>;
};

const btn = {
    padding: '10px 16px',
    fontSize: 16,
    fontFamily: 'inherit',
    backgroundColor: '#222',
    color: 'white',
    border: '2px solid white',
    borderRadius: 6,
    cursor: 'pointer',
};
